import { useTranslation } from 'react-i18next';
import { Label } from '@ooh/ui/components/label';
import { SearchableSelectField } from '@ooh/ui/components/searchable-select-field';

export interface BrandFieldProps {
  brandValue: string | null | undefined;
  onBrandChange: (value: string | undefined) => void;
  brandError?: string;
  /** The currently selected client's brands, or an empty array when no client is selected. */
  selectedClientBrands: string[];
  disabled?: boolean;
  /** Element id + the `SearchableSelectField`'s own `data-testid` (kept stable across callers). */
  id?: string;
  /** Wrapper testid — kept distinct from `id` so both elements resolve uniquely in tests. */
  fieldTestId?: string;
}

/**
 * Brand select, sourced from the selected client's brands. Extracted from
 * create-offer-page.tsx (CU-86cbc2qv8, offer-client-collaboration) so the
 * quick-add-offer-picker's "creating" mode reuses the exact same UI and
 * behavior instead of a parallel implementation.
 *
 * The inline "create new client" affordance used to live here as a separate
 * card below this field; it now lives inside the Klient field's own dropdown
 * (see `useInlineClientCreateAction`) since that's where a user actually
 * looks for it — right after searching for a client and not finding one.
 */
export function BrandField({
  brandValue,
  onBrandChange,
  brandError,
  selectedClientBrands,
  disabled = false,
  id = 'create-offer-brand',
  fieldTestId = 'field-brand',
}: BrandFieldProps) {
  const { t } = useTranslation('offers');
  const hasBrands = selectedClientBrands.length > 0;

  return (
    <div className="space-y-1.5" data-testid={fieldTestId}>
      <Label htmlFor={id}>{t('create.fields.brand')}</Label>
      <SearchableSelectField
        data-testid={id}
        disabled={!hasBrands || disabled}
        options={selectedClientBrands.map((brand) => ({ label: brand, value: brand }))}
        placeholder={
          hasBrands ? t('create.fields.brandPlaceholder') : t('create.fields.brandUnavailable')
        }
        value={brandValue ?? ''}
        onChange={(value) => onBrandChange(value || undefined)}
      />
      {brandError ? (
        <p className="text-xs text-destructive" data-testid={`${fieldTestId}-error`}>
          {brandError}
        </p>
      ) : null}
    </div>
  );
}
